import React, { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
  TAgetExternalUserPipelineStats,
  TAgetExternalUserPostStats,
  ExternalUserPipelineStats,
  ExternalUserPostStats,
} from "../../../services/externalUserPipelineAPI"

interface StatCardProps {
  title: string
  value: number | string
  subtitle?: string
  color?: "blue" | "green" | "yellow" | "red" | "purple" | "gray" | "indigo" | "teal"
  percent?: number
  icon?: React.ReactNode
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  color = "blue",
  percent,
  icon,
}) => {
  const colorClasses: Record<string, string> = {
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    green: "bg-green-50 border-green-200 text-green-800",
    yellow: "bg-yellow-50 border-yellow-200 text-yellow-800",
    red: "bg-red-50 border-red-200 text-red-800",
    purple: "bg-purple-50 border-purple-200 text-purple-800",
    gray: "bg-gray-50 border-gray-200 text-gray-800",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-800",
    teal: "bg-teal-50 border-teal-200 text-teal-800",
  }

  return (
    <div
      className={`p-5 rounded-xl border-2 ${colorClasses[color]} transition-all duration-200 hover:shadow-md`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium opacity-80">{title}</div>
        {icon && <div className="opacity-60">{icon}</div>}
      </div>
      <div className="text-3xl font-bold mt-1">
        {typeof value === "number" ? value.toLocaleString("tr-TR") : value}
      </div>
      {percent !== undefined && (
        <div className="mt-3 h-2.5 bg-white/60 rounded-full overflow-hidden">
          <div
            className="h-full bg-current opacity-60 transition-all duration-700 ease-out rounded-full"
            style={{ width: `${Math.min(percent, 100)}%` }}
          />
        </div>
      )}
      {subtitle && <div className="text-xs mt-2 opacity-70">{subtitle}</div>}
    </div>
  )
}

const PipelineStep: React.FC<{
  step: number
  title: string
  description: string
  status: "completed" | "in_progress" | "pending"
  stats?: { label: string; value: string | number; color?: string }[]
  isLast?: boolean
}> = ({ step, title, description, status, stats, isLast }) => {
  const statusConfig = {
    completed: {
      bg: "bg-green-100",
      border: "border-green-400",
      text: "text-green-800",
      icon: "✅",
      label: "Tamamlandı",
      stepBg: "bg-green-500",
    },
    in_progress: {
      bg: "bg-yellow-50",
      border: "border-yellow-400",
      text: "text-yellow-800",
      icon: "⏳",
      label: "Devam Ediyor",
      stepBg: "bg-yellow-500",
    },
    pending: {
      bg: "bg-gray-50",
      border: "border-gray-300",
      text: "text-gray-600",
      icon: "🔴",
      label: "Bekliyor",
      stepBg: "bg-gray-400",
    },
  }

  const config = statusConfig[status]

  return (
    <div className="flex gap-4">
      {/* Timeline */}
      <div className="flex flex-col items-center">
        <div
          className={`w-10 h-10 rounded-full ${config.stepBg} text-white flex items-center justify-center font-bold text-sm shadow-md`}
        >
          {step}
        </div>
        {!isLast && <div className="w-0.5 flex-1 bg-gray-300 my-1" />}
      </div>

      {/* Content */}
      <div className={`flex-1 mb-6 p-5 rounded-xl border-2 ${config.border} ${config.bg}`}>
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className={`text-base font-bold ${config.text}`}>{title}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{description}</p>
          </div>
          <span
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text} border ${config.border}`}
          >
            {config.icon} {config.label}
          </span>
        </div>
        {stats && stats.length > 0 && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
            {stats.map((stat, i) => (
              <div key={i} className="bg-white/70 rounded-lg p-3">
                <div className="text-xs text-gray-500">{stat.label}</div>
                <div className={`text-lg font-bold ${stat.color || "text-gray-800"}`}>
                  {typeof stat.value === "number" ? stat.value.toLocaleString("tr-TR") : stat.value}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const ExternalUserPipeline: React.FC = () => {
  const navigate = useNavigate()

  const [stats, setStats] = useState<ExternalUserPipelineStats | null>(null)
  const [postStats, setPostStats] = useState<ExternalUserPostStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [lastUpdated, setLastUpdated] = useState<string | null>(null)

  const fetchStats = useCallback(async () => {
    try {
      const [pipelineData, postData] = await Promise.all([
        TAgetExternalUserPipelineStats(),
        TAgetExternalUserPostStats(),
      ])
      setStats(pipelineData)
      setPostStats(postData)
      setLastUpdated(postData.lastUpdated || new Date().toISOString())
      setError("")
    } catch (err: unknown) {
      if (
        typeof err === "object" &&
        err !== null &&
        "response" in err &&
        (err as { response?: { status?: number } }).response?.status === 401
      ) {
        navigate("/login")
        return
      }
      setError("External user pipeline verileri yüklenirken hata oluştu")
    } finally {
      setIsLoading(false)
    }
  }, [navigate])

  useEffect(() => {
    fetchStats()

    const interval = setInterval(() => {
      fetchStats()
    }, 60000)

    return () => clearInterval(interval)
  }, [fetchStats])

  const totalExternalUsers = stats?.totalExternalUsers || 0
  const scrapedExternalUsers = stats?.scrapedExternalUsers || 0
  const failedExternalUsers = totalExternalUsers - scrapedExternalUsers
  const scrapePercent = totalExternalUsers
    ? Math.round((scrapedExternalUsers / totalExternalUsers) * 100)
    : 0

  const totalPosts = postStats?.totalPosts || 0
  const savedPosts = postStats?.savedPosts || 0
  const unsavedPosts = postStats?.unsavedPosts || 0
  const withImageDesc = postStats?.withImageDescriptionV4 || 0
  const withoutImageDesc = postStats?.withoutImageDescriptionV4 || 0
  const withVideoAnalysis = postStats?.withVideoAnalysis || 0
  const withoutVideoAnalysis = postStats?.withoutVideoAnalysis || 0
  const savedPercent = totalPosts ? Math.round((savedPosts / totalPosts) * 100) : 0
  const imageDescPercent = totalPosts ? Math.round((withImageDesc / totalPosts) * 100) : 0
  const videoAnalysisPercent = totalPosts ? Math.round((withVideoAnalysis / totalPosts) * 100) : 0

  // Pipeline step statuses
  const scrapeStatus: "completed" | "in_progress" | "pending" =
    totalExternalUsers === 0 ? "pending" : failedExternalUsers === 0 ? "completed" : "in_progress"

  const imageDescStatus: "completed" | "in_progress" | "pending" =
    totalPosts === 0 ? "pending" : withoutImageDesc === 0 ? "completed" : "in_progress"

  const videoAnalysisStatus: "completed" | "in_progress" | "pending" =
    totalPosts === 0 ? "pending" : withoutVideoAnalysis === 0 ? "completed" : "in_progress"

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-pink-600"></div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">External User Pipeline</h1>
          <p className="text-sm text-gray-500 mt-1">
            Harici kullanıcıların scrape ve analiz sürecini takip edin
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => fetchStats()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow transition flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Yenile
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {/* Genel Özet */}
      <div className="mb-6 p-5 bg-white rounded-xl border border-gray-200 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Genel Özet</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            title="Toplam Kullanıcı"
            value={totalExternalUsers}
            color="blue"
          />
          <StatCard
            title="Scrape Edilmiş"
            value={scrapedExternalUsers}
            subtitle={`%${scrapePercent}`}
            color="green"
            percent={scrapePercent}
          />
          <StatCard
            title="Scrape Başarısız"
            value={failedExternalUsers}
            color={failedExternalUsers > 0 ? "red" : "green"}
          />
          <StatCard
            title="Toplam Post"
            value={totalPosts}
            color="gray"
          />
          <StatCard
            title="Kaydedilen Post"
            value={savedPosts}
            subtitle={`%${savedPercent}`}
            color="green"
            percent={savedPercent}
          />
          <StatCard
            title="Kaydedilmeyen Post"
            value={unsavedPosts}
            color={unsavedPosts > 0 ? "yellow" : "green"}
          />
          <StatCard
            title="Image Description"
            value={withImageDesc}
            subtitle={`%${imageDescPercent}`}
            color="indigo"
            percent={imageDescPercent}
          />
          <StatCard
            title="Video Analizi"
            value={withVideoAnalysis}
            subtitle={`%${videoAnalysisPercent}`}
            color="purple"
            percent={videoAnalysisPercent}
          />
        </div>
      </div>

      {/* Pipeline Steps */}
      <div className="mb-6 p-5 bg-white rounded-xl border border-gray-200 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-700 mb-6">Pipeline Adımları</h2>

        <PipelineStep
          step={1}
          title="External Kullanıcı Kaydı"
          description="Harici kullanıcılar sisteme 'influencer' ön ekli telefon numarasıyla eklenir"
          status={totalExternalUsers > 0 ? "completed" : "pending"}
          stats={[
            {
              label: "Toplam Kayıtlı",
              value: totalExternalUsers,
              color: "text-blue-700",
            },
          ]}
        />

        <PipelineStep
          step={2}
          title="Sosyal Medya Scrape"
          description="Instagram ve TikTok kullanıcı adları taranır"
          status={scrapeStatus}
          stats={[
            {
              label: "Scrape Edilen",
              value: scrapedExternalUsers,
              color: "text-green-700",
            },
            {
              label: "Başarısız",
              value: failedExternalUsers,
              color: failedExternalUsers > 0 ? "text-red-700" : "text-green-700",
            },
            {
              label: "Tamamlanma",
              value: `%${scrapePercent}`,
              color: scrapePercent >= 80 ? "text-green-700" : "text-yellow-700",
            },
          ]}
        />

        <PipelineStep
          step={3}
          title="Post Toplama"
          description="Scrape edilen kullanıcıların Instagram postları toplanır"
          status={totalPosts > 0 ? "completed" : scrapedExternalUsers > 0 ? "in_progress" : "pending"}
          stats={[
            {
              label: "Toplam Post",
              value: totalPosts,
              color: "text-blue-700",
            },
          ]}
        />

        <PipelineStep
          step={4}
          title="Görsel Analizi (Image Description)"
          description="Post görsellerinin AI ile açıklaması oluşturulur (image_description_v4)"
          status={imageDescStatus}
          stats={[
            {
              label: "Analiz Yapılmış",
              value: withImageDesc,
              color: "text-green-700",
            },
            {
              label: "Analiz Eksik",
              value: withoutImageDesc,
              color: withoutImageDesc > 0 ? "text-red-700" : "text-green-700",
            },
            {
              label: "Tamamlanma",
              value: `%${imageDescPercent}`,
              color: imageDescPercent >= 80 ? "text-green-700" : "text-yellow-700",
            },
          ]}
        />

        <PipelineStep
          step={5}
          title="Video Analizi"
          description="Post videolarının analiz sonuçları işlenir (video_analysis)"
          status={videoAnalysisStatus}
          stats={[
            {
              label: "Analiz Yapılmış",
              value: withVideoAnalysis,
              color: "text-green-700",
            },
            {
              label: "Analiz Eksik",
              value: withoutVideoAnalysis,
              color: withoutVideoAnalysis > 0 ? "text-red-700" : "text-green-700",
            },
            {
              label: "Tamamlanma",
              value: `%${videoAnalysisPercent}`,
              color: videoAnalysisPercent >= 80 ? "text-green-700" : "text-yellow-700",
            },
          ]}
          isLast
        />
      </div>

      {/* Detay Tabloları */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Kullanıcı İstatistikleri */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">Kullanıcı İstatistikleri</h3>
          </div>
          <table className="min-w-full">
            <tbody className="divide-y divide-gray-100">
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-600">Toplam External Kullanıcı</td>
                <td className="px-4 py-3 text-xs font-semibold text-right">
                  {totalExternalUsers.toLocaleString("tr-TR")}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-600">Scrape Edilmiş</td>
                <td className="px-4 py-3 text-xs font-semibold text-right text-green-700">
                  {scrapedExternalUsers.toLocaleString("tr-TR")}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-600">Scrape Başarısız</td>
                <td className="px-4 py-3 text-xs font-semibold text-right text-red-700">
                  {failedExternalUsers.toLocaleString("tr-TR")}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-600">Scrape Oranı</td>
                <td className="px-4 py-3 text-xs font-semibold text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full transition-all duration-500"
                        style={{ width: `${scrapePercent}%` }}
                      />
                    </div>
                    <span className="text-gray-600 w-8 text-right">%{scrapePercent}</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Post İstatistikleri */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">Post İstatistikleri</h3>
          </div>
          <table className="min-w-full">
            <tbody className="divide-y divide-gray-100">
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-600">Toplam Post</td>
                <td className="px-4 py-3 text-xs font-semibold text-right">
                  {totalPosts.toLocaleString("tr-TR")}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-600">Kaydedilen Post</td>
                <td className="px-4 py-3 text-xs font-semibold text-right text-green-700">
                  {savedPosts.toLocaleString("tr-TR")}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-600">Kaydedilmeyen Post</td>
                <td className="px-4 py-3 text-xs font-semibold text-right text-yellow-700">
                  {unsavedPosts.toLocaleString("tr-TR")}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-600">Kaydetme Oranı</td>
                <td className="px-4 py-3 text-xs font-semibold text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full transition-all duration-500"
                        style={{ width: `${savedPercent}%` }}
                      />
                    </div>
                    <span className="text-gray-600 w-8 text-right">%{savedPercent}</span>
                  </div>
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-600">Image Description Yapılmış</td>
                <td className="px-4 py-3 text-xs font-semibold text-right text-green-700">
                  {withImageDesc.toLocaleString("tr-TR")}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-600">Image Description Eksik</td>
                <td className="px-4 py-3 text-xs font-semibold text-right text-red-700">
                  {withoutImageDesc.toLocaleString("tr-TR")}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-600">Image Description Oranı</td>
                <td className="px-4 py-3 text-xs font-semibold text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                        style={{ width: `${imageDescPercent}%` }}
                      />
                    </div>
                    <span className="text-gray-600 w-8 text-right">%{imageDescPercent}</span>
                  </div>
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-600">Video Analizi Yapılmış</td>
                <td className="px-4 py-3 text-xs font-semibold text-right text-green-700">
                  {withVideoAnalysis.toLocaleString("tr-TR")}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-600">Video Analizi Eksik</td>
                <td className="px-4 py-3 text-xs font-semibold text-right text-red-700">
                  {withoutVideoAnalysis.toLocaleString("tr-TR")}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-600">Video Analizi Oranı</td>
                <td className="px-4 py-3 text-xs font-semibold text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-500 rounded-full transition-all duration-500"
                        style={{ width: `${videoAnalysisPercent}%` }}
                      />
                    </div>
                    <span className="text-gray-600 w-8 text-right">%{videoAnalysisPercent}</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Last Updated */}
      {lastUpdated && (
        <div className="text-sm text-gray-500 text-right">
          Son güncelleme: {new Date(lastUpdated).toLocaleString("tr-TR")}
        </div>
      )}
    </div>
  )
}

export default ExternalUserPipeline
